import React, { useCallback, useEffect, useRef, useState } from 'react';

import { IsetToolbar } from '../CustomToolBar';
import {
  Dividehr,
  ToolbarButton,
  ToolbarWrapper,
} from '../../QuillToolbarStyles';
import DropdownWrapper from '@components/wrapper/DropdownWrapper';
import HrList from './HrList';
import customHr from '../../QuillCustomHr/blots';

type Props = {
  quillRef?: any;
  setToolbar?: IsetToolbar;
  handleDividerClick?: (any) => void;
};
function ToolbarSec2({ setToolbar, quillRef, handleDividerClick }: Props) {
  const { indent, divider, list, blockquote, codeblock, table } =
    setToolbar || {};
  const [showDivider, setShowDivider] = useState(false);
  // 드롭다운 열릴 시점의 에디터 selection 저장
  const savedRangeRef = useRef<{ index: number; length: number } | null>(null);

  const toggleDivider = useCallback((e?: React.MouseEvent) => {
    // DropdownWrapper 내부 클릭이 버블링될 때 이 핸들러가 중복 실행되지 않도록 방지
    if (e) {
      const target = e.target as HTMLElement;
      if (target.closest('.ql-hr-list')) return;
    }
    if (!quillRef || !quillRef.current) return;
    // 열기 직전에 현재 selection 저장
    try {
      const editor = quillRef.current.getEditor();
      const range = editor.getSelection();
      if (range) savedRangeRef.current = range;
    } catch {}
    setShowDivider((prev) => !prev);
  }, [quillRef]);

  useEffect(() => {
    if (!quillRef || !quillRef.current) return;

    const editor = quillRef.current.getEditor();
    if (!editor) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Delete' || e.key === 'Backspace') {
        const range = editor.getSelection();
        if (!range) return;

        // 현재 선택된 블록을 찾기 위해 DOM 탐색
        const [blot] = editor.getLeaf(range.index + 1);
        if (blot instanceof customHr) {
          blot.remove(); // <hr> 삭제
          e.preventDefault();
        }
      }
    };

    // hr을 클릭했을 때 생기는 border를 해제함
    const handleSelectionChange = () => {
      customHr.clearSelectedHr();
    };

    editor.on('selection-change', handleSelectionChange);
    editor.root.addEventListener('keydown', handleKeyDown);

    return () => {
      editor.off('selection-change', handleSelectionChange);
      editor.root.removeEventListener('keydown', handleKeyDown);
    };
  }, [quillRef]);

  const handleHr = useCallback(({ style }) => {
    if (!quillRef || !quillRef.current) return;

    const editor = quillRef.current.getEditor();
    // 저장된 selection 우선 사용, 없으면 현재 selection, 없으면 문서 끝
    const saved = savedRangeRef.current;
    const current = editor.getSelection();
    const range = saved || current;

    const index = range ? range.index : Math.max(0, editor.getLength() - 1);

    editor.insertEmbed(index, 'hr', { style }, 'user');

    editor.setSelection(index + 1, 0, 'silent');
    savedRangeRef.current = null;
  }, [quillRef]);

  const handleDividerSelection = useCallback(
    (style) => {
      handleHr({ style });
      setShowDivider(false);
    },
    [handleHr],
  );

  const handleTableClick = (rows, cols) => {
    if (!quillRef || !quillRef.current) return;

    let _rows = rows || 3;
    let _cols = cols || 3;

    const editor = quillRef.current.getEditor();
    const range = editor.getSelection(true); // 현재 선택 범위 가져오기

    if (range) {
      // 테이블 DOM 요소 생성
      const table = document.createElement('table');

      for (let i = 0; i < _rows; i++) {
        const row = document.createElement('tr');
        for (let j = 0; j < _cols; j++) {
          const cell = document.createElement('td');
          cell.setAttribute('contenteditable', 'true');
          cell.innerText = ''; // 빈 셀
          row.appendChild(cell);
        }
        table.appendChild(row);
      }

      // 현재 커서 위치에 테이블 삽입
      editor.clipboard.dangerouslyPasteHTML(range.index, table.outerHTML);
    }
  };

  return (
    <>
      {(blockquote || codeblock || table) && (
        <>
          <Dividehr />
          <ToolbarWrapper>
            {blockquote && (
              <ToolbarButton
                className="ql-blockquote"
                data-tooltip="인용"
              />
            )}
            {codeblock && (
              <ToolbarButton
                className="ql-code-block"
                data-tooltip="코드블럭"
              />
            )}

            {divider && (
              <ToolbarButton
                className="ql-hr"
                data-tooltip="구분선"
                style={{ position: 'relative' }}
                onClick={(e) => toggleDivider(e)}
              >
                {showDivider && (
                  <DropdownWrapper
                    className="ql-hr-list"
                    direction="topLeft"
                    style={{ width: 180, zIndex: 999 }}
                  >
                    <HrList handleDivider={handleDividerSelection} />
                  </DropdownWrapper>
                )}
              </ToolbarButton>
            )}

            {indent && (
              <ToolbarButton
                value="+1"
                className="ql-indent"
                data-tooltip="들여쓰기"
              />
            )}

            {table && (
              <ToolbarButton
                onClick={() => handleTableClick(3, 3)}
                className="ql-table"
                data-tooltip="표"
              />
            )}
            {list && (
              <>
                <ToolbarButton
                  className="ql-list"
                  value="ordered"
                  data-tooltip="순서 리스트"
                />
                <ToolbarButton
                  className="ql-list"
                  value="bullet"
                  data-tooltip="점 리스트"
                />
              </>
            )}
          </ToolbarWrapper>
          <Dividehr />
        </>
      )}
    </>
  );
}

export default ToolbarSec2;
